'use client';

import React, { JSX, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { usePathname, useSearchParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';

import { AppDispatch } from '@/store/store';

import * as _ from 'underscore';

import { API_KEY_SORT } from '@/constants/api';
import {
	CAL_DISPLAY_TYPE_MOBILE,
	CAL_DISPLAY_TYPE_MONTHLY,
	CAL_DISPLAY_TYPE_WEEKLY,
} from '@/constants/calendar';
import { 
	CSS_CALENDAR_BREAK, 
	CSS_SLIDE_DURATION,
	CSS_SLIDE_FAST_DURATION, 
} from '@/constants/css';
import { EVENT_FILTER_KEY_PRODUCT_LINE } from '@/constants/events';
import { TX_EVENTS_NO_EVENTS_SCHEDULED } from '@/constants/strings';
import { 
	URL_PARAM_DAY,
	URL_PARAM_MONTH,
	URL_PARAM_YEAR,
} from '@/constants/urls';

import useEvents from '@/hooks/useEvents';

import Month from '@/models/calendar/Month';
import StoreEvent from '@/models/events/StoreEvent';
import Week from '@/models/calendar/Week';

import { 
	eventMonthFetched,
	getDaysEvents, 
} from '@/utils/events';
import { isDateToday } from '@/utils/general';
import { getUrlParams } from '@/utils/request';

import { eventAdminFetchMonth } from '@/actions/event';

import EventCalendarControls from '@/components/Events/blocks/EventCalendarControls';
import EventCalendarDay from '@/components/Events/blocks/EventCalendarDay';
import EventCalendarEventSummary from '@/components/Events/blocks/EventCalendarEventSummary';
import EventCalendarNavigation from '@/components/Events/blocks/EventCalendarNavigation';
import LoadingIcon from '@/components/Icons/LoadingIcon';

import './style/_eventcalendar.scss';


// Prop types
interface EventCalendarProps {
	admin?: boolean;
	plPermalink?: string;
	setPlPermalink?: (permalink: string) => void;
}

interface EventModal {
	date: Date | null;
	open: boolean;
	event: StoreEvent | null;
	lastOpen: number;
	clickHeight: number;
}

export const EventCalendar = (props: EventCalendarProps): JSX.Element => {
  
	// Dispatch
	const dispatch = useDispatch<AppDispatch>();

	// Constants
	const now = new Date();

	// Props
	const {
		admin = false,
		plPermalink,
		setPlPermalink,
	} = props;

	// Internationalization
	const { t } = useTranslation();
	
	// Router
	const pathname = usePathname();
	const searchParams = useSearchParams();

	const parsedDay = parseInt(searchParams.get(URL_PARAM_DAY) || '');
	const parsedMonth = parseInt(searchParams.get(URL_PARAM_MONTH) || '');
	const parsedYear = parseInt(searchParams.get(URL_PARAM_YEAR) || '');

	// The month param is 0-indexed, so truthiness can't stand in for "param
	// present" — it dropped every January (0) deep link. A missing or garbage
	// param falls back to today's value instead.
	const startDay = Number.isNaN(parsedDay) ? now.getDate() : parsedDay;
	const startMonth = Number.isNaN(parsedMonth) ? now.getMonth() : parsedMonth;
	const startYear = Number.isNaN(parsedYear) ? now.getFullYear() : parsedYear;

	const startDate = new Date(startYear, startMonth, startDay);

	// Local state
	const [rendered, setRendered] = useState<boolean>(false);

	const [viewType, setViewType] = useState<string>(CAL_DISPLAY_TYPE_MONTHLY);
	const [lastViewType, setLastViewType] = useState<string>(CAL_DISPLAY_TYPE_MONTHLY);
	const [selectedMonth, setSelectedMonth] = useState<Month>(new Month({ month: startMonth, year: startYear }));
	const [selectedWeek, setSelectedWeek] = useState<Week>(new Week({ date: startDate }));
	const [requestPending, setRequestPending] = useState<boolean>(false);
	const [eventModal, setEventModal] = useState<EventModal>({
		date: null,
		open: false,
		event: null,
		lastOpen: 0,
		clickHeight: 0.0,
	});
	const [eventWeekIndex, setEventWeekIndex] = useState<number>(0);
	const [expanding, setExpanding] = useState<boolean>(false);
	const [expandedDate, setExpandedDateState] = useState<Date | null>(null);
	const [topLayerDate, setTopLayerDate] = useState<Date | null>(null);
	const [calendarWidth, setCalendarWidth] = useState<number>(1024);
	const [filters, setFilters] = useState<Record<string, any>>({});

	// Controller Refs
	const controllersRef = useRef<Record<string, any>>({});
	
	// DOM Refs
	const calendarBodyRef = useRef<HTMLDivElement>(null);
	const eventModalRef = useRef<HTMLDivElement>(null);

	// Timeout refs
	const expandingTimeout = useRef<NodeJS.Timeout | null>(null);
	const openTimeout = useRef<NodeJS.Timeout | null>(null);
	const closeTimeout = useRef<NodeJS.Timeout | null>(null);

	// State-tracking refs for attached methods
	const eventModalStateRef = useRef<EventModal>(eventModal);
	const expandedDateStateRef = useRef<Date | null>(expandedDate);
	const expandingStateRef = useRef<boolean>(expanding);
	const viewTypeStateRef = useRef<string>(viewType);

	// Events
	const {
		events,
		schedules,
	} = useEvents();

	// Methods
	const isMobileView = useCallback(() => {
		// All dimension checking and view setting done in checkSize
		return viewType === CAL_DISPLAY_TYPE_MOBILE;
	}, [viewType]);

	const closeEventModal = useCallback(() => {
		setEventModal({
			date: null,
			open: false,
			event: null,
			lastOpen: 0,
			clickHeight: 0.0,
		});
		window.history.replaceState(null, '', pathname);
	}, [ pathname ]);

	const openEventModal = useCallback((evt: React.MouseEvent, eventObj: StoreEvent, idx: number, evtDate: Date) => {
		
		if(!calendarBodyRef?.current || isMobileView()) {
			return null;
		}

		const percent = (evt.clientY - calendarBodyRef.current.getBoundingClientRect().top) / calendarBodyRef.current.getBoundingClientRect().height;

		let lastOpen = eventModal.open ? 0 : Date.now();
		let delayOpen = 0;

		if(eventModal.open && eventModal.event?.publicUuid === eventObj.publicUuid) {
			lastOpen = Date.now();
		} else if(eventModal.open) {
			delayOpen = 25;
			closeEventModal();
		}

		openTimeout.current = setTimeout(() => {
			setEventModal({
				date: evtDate,
				open: true,
				event: eventObj,
				lastOpen: lastOpen,
				clickHeight: percent,
			});
			setEventWeekIndex(idx);
			window.history.replaceState(null, '', pathname + getUrlParams(eventObj.getUrlParams()));
		}, delayOpen);
		
	}, [ calendarBodyRef, isMobileView, eventModal , closeEventModal, pathname ]);

	// Branch on the ref rather than the `expandedDate` binding: checkClick is
	// memoised with no deps to keep the document listener's identity stable, so
	// it permanently holds render 0's copy of this function, where the binding
	// is still null. That sent every click-away collapse down the expand branch,
	// dropping the top layer immediately instead of holding it through the
	// slide-out. Reading the ref keeps this function closure-independent, so a
	// stale copy behaves exactly like a fresh one.
	const setExpandedDate = (day: Date | null) => {
		if(expandedDateStateRef.current === null) {

			setExpanding(true);
			setExpandedDateState(day);
			setTopLayerDate(day);

			expandingTimeout.current = setTimeout(() => {
				setExpanding(false);
			}, CSS_SLIDE_DURATION);

		} else {

			setExpandedDateState(null);

			closeTimeout.current = setTimeout(() => {
				setTopLayerDate(null);
			}, CSS_SLIDE_FAST_DURATION);
		}
	}

	const checkClick = useCallback((evt: MouseEvent) => {

		const OPEN_DELAY = 100;

		if(eventModalStateRef.current.open && eventModalStateRef.current.lastOpen < Date.now() - OPEN_DELAY) {
			let targetElement = evt.target as HTMLElement;
			do {
				if(targetElement === eventModalRef?.current) {
					return null;
				}
				targetElement = targetElement?.parentNode as HTMLElement;
			} while (targetElement);
			closeEventModal();
		}

		if(
			expandedDateStateRef.current !== null && 
			expandingStateRef.current === false && 
			viewTypeStateRef.current === CAL_DISPLAY_TYPE_MONTHLY
		) {
			let targetElement = evt.target as HTMLElement;

			do {
				try {
					if(targetElement.classList.contains('ecCalDay') && targetElement.classList.contains('expanded')) {
						return null;
					}
				} catch {
					// Do nothing
				}
				targetElement = targetElement.parentNode as HTMLElement;
			} while (targetElement);
			setExpandedDate(null);
		}
	}, []);

	const setDisplayMode = (mode: string) => {
		const lastViewValue = viewType !== CAL_DISPLAY_TYPE_MOBILE ? viewType : lastViewType;
		setViewType(mode);
		setLastViewType(lastViewValue);
	};

	const checkSize = useCallback(() => {

		const WIDTH_HYSTERESIS = 15;

		if(!calendarBodyRef?.current) {
			return null;
		}

		const calWidth = calendarBodyRef.current.getBoundingClientRect().width;
		if(!calWidth) {
			return null;
		}

		setCalendarWidth(calWidth);

		if(calWidth < (CSS_CALENDAR_BREAK - WIDTH_HYSTERESIS) && isMobileView() === false) {
			setDisplayMode(CAL_DISPLAY_TYPE_MOBILE);
			closeEventModal();
		} else if(calWidth > (CSS_CALENDAR_BREAK + WIDTH_HYSTERESIS) && isMobileView() === true) {
			setDisplayMode(lastViewType);
		}
	}, [isMobileView, closeEventModal])
	
	const checkSizeThrottled = useCallback(_.throttle(checkSize, 100), [checkSize]);

	const getEventForMonth = useCallback(async (
		month = selectedMonth, 
		sort = null, 
		filters = {}, 
		forceReq = false
	) => {

		if(forceReq === false && eventMonthFetched(month, events)) {
			return null;
		}

		if(controllersRef?.current[month.year] && controllersRef?.current[month.year][month.monthIndex]) {
			controllersRef?.current[month.year][month.monthIndex].abort();
		}

		if(!controllersRef?.current[month.year]) {
			controllersRef.current = { ...controllersRef.current, [month.year]: {} };
		}

		const controller = new AbortController();
		controllersRef.current = { 
			...controllersRef.current, 
			[month.year]: { 
				...controllersRef.current[month.year], 
				[month.monthIndex]: controller 
			}
		};

		try {
			// Add the +1 for backend since python month index starts at 1, not 0
			const controlParams: Record<string, string | number> = {
				month: month.monthIndex + 1,
				year: month.year,
			};

			if (sort) {
				controlParams[API_KEY_SORT] = sort;
			}

			const getParams = Object.assign({}, filters, controlParams);
			await dispatch(eventAdminFetchMonth(getParams, controller.signal));

		} catch(err) {
			if(!controller?.signal?.aborted) {
				console.error(err);
			}
		}
	// `events` must be a dependency or the fetched-month dedupe compares
	// against the initial empty store forever and every navigation refetches
	// all five months.
	}, [dispatch, events]);

	// The target month is a parameter rather than read from state:
	// setDisplayMonth/setDisplayWeek invoke the refreshData instance memoized
	// on the PREVIOUS render, so reading selectedMonth through the closure
	// fetched around the OLD month. A one-step navigation masked it (the old
	// ±2 window covers the new month), but a bigger jump — the Today button
	// from a deep-linked month — rendered a month no fetch ever covered,
	// permanently empty.
	const refreshData = useCallback(async (forceReq: boolean = false, mon: Month = selectedMonth) => {

		if(!eventMonthFetched(mon, events)) {
			setRequestPending(true);
		}

		await getEventForMonth(mon, null, {}, forceReq);
		await getEventForMonth(mon.nextMonth(), null, {}, forceReq);
		await getEventForMonth(mon.lastMonth(), null, {}, forceReq);

		setRequestPending(false);

		await getEventForMonth(mon.nextMonth().nextMonth(), null, {}, forceReq);
		await getEventForMonth(mon.lastMonth().lastMonth(), null, {}, forceReq);
	}, [events, getEventForMonth, selectedMonth]);

	const setFilterValue = useCallback((key: string, val: any, cb?: () => any) => {

		if(key === EVENT_FILTER_KEY_PRODUCT_LINE && setPlPermalink) {
			setPlPermalink(val);
		}

		setFilters((prevFilters) => ({ ...prevFilters, [key]: val }));

		if (cb) {
			// Ensure callback runs after state is updated
			setTimeout(() => { cb(); }, 0);
		}
	}, [setPlPermalink]);

	const getWeekWrapperClass = (weekIndex: number): string => {

		const baseClass = 'ecCalWeek';

		if(isMobileView()) {
			return `${baseClass} mobileWeek`;
		}

		const isWeekViewClass = viewType === CAL_DISPLAY_TYPE_WEEKLY ? ' finalWeek weekView' : '';
		const isFirstWeekClass = viewType === CAL_DISPLAY_TYPE_MONTHLY && weekIndex === 0 ? ' firstWeek' : '';
		const isLastWeekClass = viewType === CAL_DISPLAY_TYPE_MONTHLY && selectedMonth.weeksCount === weekIndex + 1 ? ' finalWeek' : '';

		return `${baseClass}${isWeekViewClass}${isFirstWeekClass}${isLastWeekClass}`;
	}

	const isDayExpanded = (day: Date): boolean => {
		
		if(expandedDate === null) { return false; }

		try {
			if(day.getFullYear() === expandedDate.getFullYear() && day.getMonth() === expandedDate.getMonth() && day.getDate() === expandedDate.getDate()) {
				return true;
			}
		} catch {
			return false;
		}
		return false;
	}

	const isDayTopLayer = (day: Date): boolean => {
		if(topLayerDate === null) { return false; }

		try {
			if(day.getFullYear() === topLayerDate.getFullYear() && day.getMonth() === topLayerDate.getMonth() && day.getDate() === topLayerDate.getDate()) {
				return true;
			}
		} catch {
			return false;
		}
		return false;
	}

	const getDayWrapperClass = (day: Date, dayIndex: number): string => {

		const baseClass = 'ecCalDay';

		const firstOfWeekClass = dayIndex === 0 ? ' firstOfWeek' : '';
		const isTodayClass = isDateToday(day) ? ' ecCalToday' : '';
		const lastOfWeekClass = dayIndex === 6 && !isMobileView() ? ' lastOfWeek' : '';
		const mobileClass = isMobileView() ? ' mobileDay' : '';
		const isExpandedClass = isDayExpanded(day) ? ' expanded' : '';
		const isTopLayerClass = isDayTopLayer(day) ? ' topped' : '';

		return `${baseClass}${firstOfWeekClass}${isTodayClass}${lastOfWeekClass}${mobileClass}${isExpandedClass}${isTopLayerClass}`;
	}

	const setDisplayMonth = useCallback((mon: Month) => {
		setSelectedMonth(mon);
		setSelectedWeek(new Week({ date: mon.firstDate }));
		setTimeout(() => { refreshData(false, mon); }, 0);
	}, [ refreshData ]);

	const setDisplayWeek = useCallback((week: Week) => {
		const mon = new Month({ monthIndex: week.monthIndex, year: week.year });
		setSelectedMonth(mon);
		setSelectedWeek(week);
		setTimeout(() => { refreshData(false, mon); }, 0);
	}, [ refreshData ]);

	const getWeekArray = (): Week[] => {
		if(viewType === CAL_DISPLAY_TYPE_MONTHLY) {
			return selectedMonth.calendarWeeks();
		} else if(viewType === CAL_DISPLAY_TYPE_WEEKLY) {
			return [ selectedWeek ];
		} else if(viewType === CAL_DISPLAY_TYPE_MOBILE) {
			return selectedMonth.calendarWeeks();
		}
		return [];
	}

	const getModalWrapperClass = () => {

		const baseClass = 'ecEventModalWrapper';
		const openValue = eventModal.clickHeight && eventModal.clickHeight > 0.5 ? 'openBottom' : 'open';

		const openClass = eventModal.open ? ` ${openValue}` : ``;
		const indexClass = eventWeekIndex === 0 || eventWeekIndex > 0 ? ` idx${eventWeekIndex}` : ``;

		return `${baseClass}${openClass}${indexClass}`;
	}

	// Effects
	useEffect(() => {		
		return () => {
			if(expandingTimeout.current) clearTimeout(expandingTimeout.current);
			if(openTimeout.current) clearTimeout(openTimeout.current);
			if(closeTimeout.current) clearTimeout(closeTimeout.current);
		};
	}, []);
	
	useEffect(() => {
		if(!rendered) {
			refreshData(true);
			setRendered(true);
		}
	}, [refreshData, rendered]);

	useEffect(() => {

		// Initial load/unload for checkClick;
		// It doesn't have callback dependencies, but it may in the future

		document.addEventListener('click', checkClick, false);
		return () => {
			document.removeEventListener('click', checkClick, false);
		};
	}, [checkClick]);

	useEffect(() => {

		window.addEventListener('resize', checkSizeThrottled, false);

		// checkSize();

		return () => {
			window.removeEventListener('resize', checkSizeThrottled, false);
		};
	}, [checkSize, checkSizeThrottled]);

	useEffect(() => {

		// Callback to parent if product line changes

		if(plPermalink) {
			setFilterValue(EVENT_FILTER_KEY_PRODUCT_LINE, plPermalink);
		}
	}, [plPermalink, setFilterValue]);

	useEffect(() => {
		// Hack to track state in checkClick without constantly detactching and reattaching listener
		eventModalStateRef.current = eventModal;
		expandedDateStateRef.current = expandedDate;
		expandingStateRef.current = expanding;
		viewTypeStateRef.current = viewType;
	}, [eventModal, expandedDate, expanding, viewType]);
	
	let daysRendered = 0;

	// Render
	return (
		<div className={`EventCalendar ${admin ? 'adminTheme' : ''}`} ref={calendarBodyRef}>
				<div className='ecLiner'>
					<div className='ecNavWrapper'>
						<EventCalendarNavigation
							admin={admin}
							displayMode={viewType}
							displayMonth={selectedMonth}
							displayWeek={selectedWeek}
							setMonth={setDisplayMonth}
							setWeek={setDisplayWeek} />
					</div>
					<div className='ecControlsWrapper'>
						<EventCalendarControls
							adminTheme={admin}
							displayMode={viewType}
							filters={filters}
							setDisplayMode={setDisplayMode}
							setFilter={setFilterValue}
							width={calendarWidth} />
					</div>
					<div className='ecCalWrapper'>
						<div className='ecCalLiner'>
							{isMobileView() === false ?
								<>
									{getWeekArray().map((week: Week, i: number) => {
										return <div className={getWeekWrapperClass(i)} key={`wk${i}mon${selectedMonth.monthIndex}`}>
											{week.days.map((day: Date, j: number) => {
												return <div className={getDayWrapperClass(day, j)} key={`day${j}wk${i}mon${selectedMonth.monthIndex}`}>
													<EventCalendarDay
														admin={admin}
														date={day}
														displayMode={viewType}
														displayMonth={selectedMonth}
														expanded={isDayExpanded(day)}
														filters={filters}
														firstWeek={i === 0}
														loading={requestPending}
														openModal={openEventModal}
														setExpanded={setExpandedDate}
														weekIndex={j} />
												</div>
											})}
										</div>
									})}
								</> :
								<>
									<div className={getWeekWrapperClass(0)}>
										{selectedMonth.days.map((day: Date, k: number) => {
											
											if(getDaysEvents(day, events, schedules, filters).length === 0 && !isDateToday(day)) {
												return null;
											}

											daysRendered++;

											return <div className={getDayWrapperClass(day, k)} key={`day${k}wkMmon${selectedMonth.monthIndex}`}>
												<EventCalendarDay
													admin={admin}
													date={day}
													displayMode={viewType}
													displayMonth={selectedMonth}
													filters={filters}
													firstWeek={true}
													loading={requestPending}
													openModal={openEventModal}
													weekIndex={0} />
											</div>
										})}
										{isMobileView() && daysRendered === 0 ?
											<div className='noDaysRendered'>
												{t(TX_EVENTS_NO_EVENTS_SCHEDULED)}
											</div> :
											null
										}
									</div>
								</>
							}
						</div>
						{!isMobileView() && (
							<div className={getModalWrapperClass()} ref={eventModalRef}>
								<EventCalendarEventSummary
									adminTheme={admin}
									closeAction={closeEventModal}
									date={eventModal.date}
									displayMode={viewType}
									eventObj={eventModal.event} />
							</div>
						)}
						{requestPending && (
							<div className='ecLoadingOverlay'>
								<div className='ecLoadingIconWrapper'>
									<LoadingIcon />
								</div>
							</div>
						)}
					</div>
				</div>
		</div>
  	);
};

export default EventCalendar;
